import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Blocks in-app React Router navigation AND browser unload/refresh when
 * `isDirty` is true. Works with BrowserRouter (no data router required).
 *
 * Intercepts navigator.push / navigator.replace so that <Link> clicks and
 * navigate() calls are blocked when dirty. Browser back/forward are not
 * blocked (requires a data router for that). Page close/refresh is blocked
 * via the native beforeunload dialog.
 *
 * Usage:
 *   const blocker = useUnsavedChanges(items.length > 0);
 *   <UnsavedChangesDialog
 *     open={blocker.state === 'blocked'}
 *     onDiscard={() => blocker.proceed?.()}
 *     onCancel={() => blocker.reset?.()}
 *   />
 */
export function useUnsavedChanges(isDirty) {
  const { navigator } = useContext(NavigationContext);
  const [state, setState] = useState('unblocked');
  const isDirtyRef = useRef(isDirty);
  const pendingNavRef = useRef(null);
  const bypassRef = useRef(false);

  // Keep ref in sync so that patched navigator functions always see latest value.
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args) => {
      if (isDirtyRef.current && !bypassRef.current) {
        pendingNavRef.current = { type: 'push', args };
        setState('blocked');
        return;
      }
      originalPush(...args);
    };

    navigator.replace = (...args) => {
      if (isDirtyRef.current && !bypassRef.current) {
        pendingNavRef.current = { type: 'replace', args };
        setState('blocked');
        return;
      }
      originalReplace(...args);
    };

    // Block browser close / tab refresh
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, navigator]);

  const proceed = useCallback(() => {
    const nav = pendingNavRef.current;
    pendingNavRef.current = null;
    bypassRef.current = true;
    setState('unblocked');
    if (nav?.type === 'push') {
      navigator.push(...nav.args);
    } else if (nav?.type === 'replace') {
      navigator.replace(...nav.args);
    }
  }, [navigator]);

  const reset = useCallback(() => {
    pendingNavRef.current = null;
    bypassRef.current = false;
    setState('unblocked');
  }, []);

  const bypass = useCallback(() => {
    bypassRef.current = true;
  }, []);

  return { state, proceed, reset, bypass };
}

export default useUnsavedChanges;
